import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/DropdownMenu';
import useFormatDateTime from '@/hooks/useFormatDateTime';
import Account from '@/icons/account.svg';
import Delete from '@/icons/delete.svg';
import Edit from '@/icons/edit.svg';
import Folder from '@/icons/folder.svg';
import Mail from '@/icons/mail.svg';
import More from '@/icons/more.svg';
import { useRoles } from '@/pages/Collaborators/CollaboratosModal/hooks';

import { useActions } from './hooks';

export function EmailCell({ row }) {
	let { email } = row.original;
	if (email.length > 25) {
		email = `${email.substring(0, 10)}...${email.substr(email.length - 10)}`;
	}
	return (
		<div className="flex items-center gap-4">
			<span className="text-xs font-semibold leading-5">{email}</span>
		</div>
	);
}

export function RoleCell({ row }) {
	const { role } = row.original;
	const roles = useRoles();
	const roleLabel = roles[role];

	return (
		<div className="flex items-center">
			<Account className="mx-1 h-[18px] w-[18px] text-grey-500" />
			<span className="text-xs font-semibold leading-5 text-grey-500">
				{roleLabel || roles[1]}
			</span>
		</div>
	);
}

export function FoldersCell({ row, totalFolders }) {
	const { t } = useTranslation();
	const { folderIds } = row.original;
	const { isOwner } = useActions({ row });
	const isAllFolders = isOwner || folderIds?.length === totalFolders;
	const count = folderIds.length;
	const getFolderText = () => {
		if (isAllFolders) {
			return t('users.table.allFolders');
		}
		return count === 1
			? t('users.table.folderSingular')
			: t('users.table.folderPlural', { count });
	};

	return (
		<div className="flex items-center">
			<Folder className="mx-1 size-4 text-sm text-grey-500" />
			<p className="text-xs leading-5 text-grey-500">{getFolderText()}</p>
		</div>
	);
}

export function DateCell(date) {
	const formatDateTime = useFormatDateTime();
	const currentDate = date ? formatDateTime(date) : '-';

	return (
		<div className="flex items-center">
			<span className="text-xs leading-5 text-grey-500">{currentDate}</span>
		</div>
	);
}

export function CreatedAtCell({ row }) {
	return DateCell(row.original.createdAt);
}

export function LastLoginCell({ row }) {
	return DateCell(row.original.lastLogin);
}

const getStatusStyles = isActive => {
	if (isActive) return 'bg-green-100 text-green';
	return 'text-[#e39736] bg-[#fdf7ec]';
};

export function StateCell({ row }) {
	const { t } = useTranslation();
	const { lastLogin } = row.original;
	const isActive = !!lastLogin;
	return (
		<div
			className={`inline-flex items-center justify-center rounded-md px-3 py-1 text-xs font-semibold ${getStatusStyles(isActive)}`}
		>
			{isActive ? t('users.table.active') : t('users.table.pending')}
		</div>
	);
}

export function ButtonsCell({ row }) {
	const {
		handleSendInvitation,
		handleDeleteCollaborator,
		handleEditCollaborator,
		isDisabled,
		isOwner
	} = useActions({ row });

	if (isOwner) {
		return;
	}

	return (
		<div className="flex items-center gap-2">
			<Button
				size="iconMd"
				variant="ghost"
				onClick={handleEditCollaborator}
				disabled={isDisabled}
			>
				<Edit className="size-5" />
			</Button>

			<Button
				size="iconMd"
				variant="ghost"
				onClick={handleSendInvitation}
				disabled={isDisabled}
			>
				<Mail className="size-5" />
			</Button>

			<Button
				size="iconMd"
				variant="ghost"
				onClick={handleDeleteCollaborator}
				disabled={isDisabled}
			>
				<Delete className="size-5" />
			</Button>
		</div>
	);
}

function Actions({ row, isTablet }) {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const { t } = useTranslation();
	const {
		handleSendInvitation,
		handleDeleteCollaborator,
		handleEditCollaborator,
		isDisabled,
		isOwner
	} = useActions({ row });

	if (isOwner) {
		return;
	}

	if (isTablet) {
		return (
			<div className="flex items-center gap-2">
				<Button
					variant="ghost"
					className="h-8 w-8 p-0"
					onClick={handleEditCollaborator}
					disabled={isDisabled}
				>
					<Edit className="h-5 w-5" />
				</Button>
				<Button
					variant="ghost"
					className="h-8 w-8 p-0"
					onClick={handleSendInvitation}
					disabled={isDisabled}
				>
					<Mail className="h-5 w-5" />
				</Button>
				<Button
					variant="ghost"
					className="h-8 w-8 p-0"
					onClick={handleDeleteCollaborator}
					disabled={isDisabled}
				>
					<Delete className="h-5 w-5" />
				</Button>
			</div>
		);
	}

	return (
		<DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" className="h-8 w-8 p-0">
					<More className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				side="bottom"
				forceMount
				className="px-0 py-3"
			>
				<DropdownMenuItem
					className="dropdown-item"
					onClick={handleEditCollaborator}
					disabled={isDisabled}
				>
					<div className="flex items-center gap-2 pl-2 pr-0 text-xs text-gray-500">
						<Edit className="w-[18px]" />
						{t('common.edit')}
					</div>
				</DropdownMenuItem>
				<DropdownMenuItem
					className="dropdown-item"
					onClick={handleSendInvitation}
					disabled={isDisabled}
				>
					<div
						size="16px"
						className="flex items-center gap-2 pl-2 pr-0 text-xs text-gray-500"
					>
						<Mail className="w-[18px]" />
						{t('users.table.sendEmail')}
					</div>
				</DropdownMenuItem>
				<div className="my-1 h-[1px] w-full bg-grey-200" />
				<DropdownMenuItem
					className="dropdown-item"
					onClick={handleDeleteCollaborator}
					disabled={isDisabled}
				>
					<div className="flex items-center gap-2 pl-2 pr-0 text-xs text-gray-500">
						<Delete className="w-[18px]" />
						{t('common.delete')}
					</div>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

export function MobileView({ row, totalFolders, isTablet }) {
	const { t } = useTranslation();
	const formatDateTime = useFormatDateTime();

	return (
		<div className="flex w-full flex-col gap-2">
			<div className="flex flex-row justify-between">
				<EmailCell row={row} />
				<Actions row={row} isTablet={isTablet} />
			</div>
			<RoleCell row={row} />
			<div>
				<p className="text-xs leading-5 text-grey-500">
					{t('users.table.header.createdAt')}:{' '}
					{row.original.createdAt
						? formatDateTime(row.original.createdAt)
						: '-'}
				</p>
				<p className="text-xs leading-5 text-grey-500">
					{t('users.table.header.lastLogin')}:{' '}
					{row.original.lastLogin
						? formatDateTime(row.original.lastLogin)
						: '-'}
				</p>
			</div>
			<div className="flex flex-row justify-between gap-3 md:lacks-hover:justify-start">
				<FoldersCell row={row} totalFolders={totalFolders} />
				<StateCell row={row} />
			</div>
		</div>
	);
}
